import {
  InMemoryCache,
  ApolloClient,
  ApolloLink,
  HttpLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { default as ApolloLinkTimeout } from 'apollo-link-timeout';
import { possibleTypes } from 'App/possibleTypes';
import { getMSTeamsDevice, isMSTeams } from 'utils/MSTeams';
import { getUserLocale, shouldResetLocale } from '../i18n/getUserLocale';
import { SESSION_ID_KEY } from './Tracking/serverside';
import { AUTHENTICATED_QUERY } from './Auth/authenticatedQuery';

const staticUserLocale = getUserLocale();

const userLocale = () =>
  shouldResetLocale() ? getUserLocale() : staticUserLocale;

export type GraphQLException = {
  graphQLErrors: GraphQLError[];
};

export type GraphQLError = {
  name: string;
  message?: string;
};

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI,
});

export interface ApolloCacheShape {
  authenticated: boolean;
}

/*
  ToDo: possibleTypes should be auto-generated in future.
  If there are any changes related to union or interface types in your schema, you will have to update the fragment matcher accordingly.
  More details here:
  https://www.apollographql.com/docs/react/data/fragments/#generating-possibletypes-automatically
*/
const cache = new InMemoryCache({ possibleTypes });

const authLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-api-key': localStorage.getItem('token'),
  },
}));

const getClientPlatform = () => {
  if (isMSTeams()) {
    return 'ms-teams';
  }

  return 'web';
};

const interactionSessionLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'client-platform': getClientPlatform(),
    'client-os': isMSTeams() ? getMSTeamsDevice() : 'web',
    'x-interaction-session': localStorage.getItem(SESSION_ID_KEY) || null,
  },
}));

const userLocaleLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    'x-user-locale': userLocale(),
  },
}));

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors && graphQLErrors.length > 0) {
    if (
      graphQLErrors.findIndex(error => error.name === 'UnauthorizedError') >=
        0 ||
      graphQLErrors.findIndex(error => error.name === 'TokenExpiredError') >= 0
    ) {
      cache.writeQuery({
        data: {
          authenticated: false,
        },
        query: AUTHENTICATED_QUERY,
      });
    }
  }
});

const timeoutLink = new ApolloLinkTimeout(10000);

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([
    timeoutLink.concat(authLink),
    timeoutLink.concat(interactionSessionLink),
    userLocaleLink,
    errorLink,
    timeoutLink.concat(httpLink),
  ]),
  name: 'web',
  resolvers: {},
  version: '2.0.0',
});

export default client;
